import { Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { Icon, StatusChip } from '@mysaiva/core'
import { formatDate, formatRelative, scopePreview } from '../../api-keys/format'
import type { ApiKey } from '../../api-keys/apiKeys.types'
import styles from './ApiKeys.module.scss'

interface ApiKeysTableProps {
  keys: ApiKey[]
  onOpen: (key: ApiKey) => void
}

export function ApiKeysTable({ keys, onOpen }: ApiKeysTableProps) {
  const { t } = useTranslation()

  const columns: ColumnsType<ApiKey> = [
    {
      key: 'name',
      title: t('developerPlatform.apiKeys.columns.name'),
      render: (_, key) => (
        <div>
          {/* A real button so keyboard users can reach the detail page; its
              click bubbles to the row handler, which stays the mouse shortcut. */}
          <button
            type="button"
            className={styles.cellNameButton}
            data-testid={`api-key-open-${key.id}`}
          >
            {key.name}
          </button>
          <div className={styles.cellMeta}>
            {key.revokedAt
              ? t('developerPlatform.apiKeys.meta.revoked', { date: formatDate(key.revokedAt) })
              : t('developerPlatform.apiKeys.meta.created', { date: formatDate(key.createdAt) })}
          </div>
        </div>
      )
    },
    {
      key: 'key',
      title: t('developerPlatform.apiKeys.columns.key'),
      render: (_, key) => <code className={styles.mono}>{key.maskedKey}</code>
    },
    {
      key: 'environment',
      title: t('developerPlatform.apiKeys.columns.environment'),
      render: (_, key) => t(`developerPlatform.apiKeys.environment.${key.environment}`)
    },
    {
      key: 'scopes',
      title: (
        <span className={styles.columnTitle}>
          <Icon icon="Filters3" size={16} />
          {t('developerPlatform.apiKeys.columns.scopes')}
        </span>
      ),
      render: (_, key) => (
        <div>
          <div className={styles.cellScopes}>
            {t('developerPlatform.apiKeys.meta.scopeCount', { count: key.scopes.length })}
          </div>
          <div className={styles.cellMeta}>{scopePreview(key.scopes)}</div>
        </div>
      )
    },
    {
      key: 'lastUsed',
      title: t('developerPlatform.apiKeys.columns.lastUsed'),
      render: (_, key) => (key.lastUsedAt ? formatRelative(key.lastUsedAt) : '-')
    },
    {
      key: 'status',
      title: (
        <span className={styles.columnTitle}>
          <Icon icon="Filters3" size={16} />
          {t('developerPlatform.apiKeys.columns.status')}
        </span>
      ),
      render: (_, key) => (
        <StatusChip status={key.status} data-testid={`key-status-${key.id}`}>
          {t(`developerPlatform.apiKeys.status.${key.status}`)}
        </StatusChip>
      )
    }
  ]

  return (
    <div className={styles.tableWrap} data-testid="api-keys-table">
      <Table<ApiKey>
        rowKey="id"
        columns={columns}
        dataSource={keys}
        pagination={false}
        onRow={key => ({
          onClick: () => onOpen(key),
          'data-testid': `api-key-row-${key.id}`
        })}
      />
    </div>
  )
}
